import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { toast } from 'sonner'
import { LmsPrintPreviewPdfButton } from '@/components/admin/print/LmsPrintPreviewPdfButton'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import { usePersistedGradingMode } from '@/hooks/coaching/usePersistedGradingMode'
import {
  useCompleteTest,
  useEnsureTest,
  useGradeTestItem,
  useRetakeTest,
  useTestDetail,
} from '@/hooks/coaching/useTests'
import type { LcTestDetailView, LcTestItemDetailView, TestSelection } from '@/types/coaching'
import { parseStoredAnswer } from './component-details/parts/answerArray'
import { ContentManagementPanel } from './ContentManagementPanel'
import { ConfirmDialog } from './ConfirmDialog'
import { GradingModeSwitch } from './GradingModeSwitch'
import type { QuizFocusRequest } from './grading-sheet/coachingFocus'
import { TestGradingSheet } from './grading-sheet/TestGradingSheet'
import {
  QuestionGradingList,
  type QuestionGradingItem,
} from './QuestionGradingList'

interface Props {
  learnerId: number
  readOnly?: boolean
  timeslotId?: number | null
  selection: TestSelection
  testIdOverride?: number
  /**
   * 완료 테스트의 [재응시] 버튼. 누적 테스트에 재응시가 없어 챕터만 있으면 혼란을 주므로 기본 숨김.
   * 재응시 API·훅은 유지하며, 다시 열 때는 호출부에서 true 를 넘긴다.
   */
  allowRetake?: boolean
  /** 누적(레슨 테스트)은 회차가 항상 하나라 회차·문제 수 카드가 아래 채점표와 겹친다(ADR 0011). */
  showContentManagement?: boolean
  /**
   * 누적 평가 안의 레슨 테스트는 개별 리포트가 아니라 평가 전체의 누적 테스트 리포트로 간다. 그 버튼은
   * 평가 헤더가 해소 시점에 연다(ADR 0012)라서 여기서는 숨긴다.
   */
  showReportButton?: boolean
  onChanged?: () => void
}

function toQuestionGradingItem(item: LcTestItemDetailView): QuestionGradingItem {
  return {
    quizId: item.quizId,
    gradedIsCorrect: item.gradedIsCorrect,
    studentAnswer: parseStoredAnswer(item.answer),
    corrects: item.corrects,
    questionSnapshot: item.questionSnapshot,
    correctAnswer: item.correctAnswer,
    explanation: item.explanation,
  }
}

function allItemsGraded(test: LcTestDetailView | undefined): boolean {
  return test != null
    && test.items.length > 0
    && test.items.every((item) => item.gradedIsCorrect != null)
}

/**
 * 챕터/누적 테스트 채점 영역(교사 채점 전용).
 * 트리 노드 선택 → 출제 → 채점모드(기본): 채점표 O/X 일괄 저장 / 코칭모드: 한 문항씩 답안 입력·자동채점
 * (서술형은 수동 정오답) → [채점 완료]로 점수 확정.
 * 채점표 [답안 편집]은 코칭모드로 전환해 해당 문항을 연다. [문제 출력](실 PDF)은 Phase 5c.
 */
export function TestCoachingDetail({
  learnerId,
  readOnly = false,
  timeslotId,
  selection,
  testIdOverride,
  allowRetake = false,
  showContentManagement = true,
  showReportButton = true,
  onChanged,
}: Props) {
  const { t } = useTranslation('coaching')
  const qc = useQueryClient()
  const ensure = useEnsureTest(learnerId, selection, { enabled: !readOnly })
  const [retakeTestId, setRetakeTestId] = useState<number | null>(null)
  const [reportPromptOpen, setReportPromptOpen] = useState(false)
  // 채점/코칭 모드(레슨 화면과 같은 교사별 선호 키) + 채점표 [답안 편집] → 코칭모드 문항 선택 요청.
  const { mode: gradingMode, setMode: setGradingMode } = usePersistedGradingMode()
  const [focusRequest, setFocusRequest] = useState<QuizFocusRequest | null>(null)
  // 채점표 일괄 저장 시작 시점의 "전 문항 채점" 여부 — 저장 후 안내를 한 번만 띄우기 위해 기록한다.
  const sheetBatchStartAllGraded = useRef<boolean | null>(null)
  useEffect(() => {
    setRetakeTestId(null)
    setReportPromptOpen(false)
    setFocusRequest(null)
  }, [selection.kind, selection.chapterOrdinal, selection.scopeContentId])
  const testId = retakeTestId ?? testIdOverride ?? ensure.data?.testId
  const detail = useTestDetail(learnerId, testId)
  const grade = useGradeTestItem(learnerId, testId ?? 0)
  const sheetGrade = useGradeTestItem(learnerId, testId ?? 0, { refreshTests: false })
  const complete = useCompleteTest(learnerId, testId ?? 0)
  const retake = useRetakeTest(learnerId)

  if (readOnly && testId == null) return <p role="status" className="text-sm text-muted-foreground">{t('todayGrading.readOnlyTest')}</p>
  if (ensure.isLoading || (testId != null && detail.isLoading)) {
    return (
      <section className="rounded-lg border border-gray-200 p-6" role="status" aria-live="polite">
        <p className="text-sm text-gray-500">{t('test.loading')}</p>
      </section>
    )
  }
  if (ensure.error || detail.error) {
    return (
      <section className="rounded-lg border border-gray-200 p-6" role="alert">
        <p className="text-sm text-red-600">{t('test.loadError')}</p>
      </section>
    )
  }
  const data = detail.data
  if (!data) {
    return (
      <section className="rounded-lg border border-gray-200 p-6" role="status" aria-live="polite">
        <p className="text-sm text-gray-500">{t('test.loading')}</p>
      </section>
    )
  }

  const title = data.kind === 'CHAPTER_COMPREHENSIVE'
    ? t('test.cumulativeTitle', { chapter: data.chapterOrdinal })
    : t('test.chapterTitle', { chapter: data.chapterOrdinal })
  const isAllGraded = allItemsGraded(data)
  const statusKey = data.status === 'COMPLETED'
    ? 'completed'
    : isAllGraded
      ? 'graded'
      : data.status === 'IN_PROGRESS'
        ? 'inProgress'
        : 'generated'
  const chapterReportContext = timeslotId == null ? '' : `?timeslotId=${timeslotId}`
  const isChapterTest = data.kind === 'CHAPTER'
  const canCompleteTest = !readOnly && isAllGraded
  const canEditReport = isAllGraded
  const completeLabel = isChapterTest
    ? t('test.completeChapter')
    : t('test.completeCumulative')
  const reportLabel = isChapterTest
    ? t('chapterReport.editTitle')
    : t('test.editCumulativeReport')
  const reportUrl = isChapterTest
    ? `/admin/coaching/chapter-report/${learnerId}/${data.testId}${chapterReportContext}`
    : `/admin/coaching/test-report/${learnerId}/${data.testId}`
  const openReportEditor = () => {
    window.open(reportUrl, '_blank', 'noopener,noreferrer')
  }
  /** 미채점 → 전 문항 채점 전이 시 1회: 챕터는 리포트 편집 안내 다이얼로그, 누적은 토스트. */
  const announceIfNewlyAllGraded = (wasAllGraded: boolean, updated: LcTestDetailView | undefined) => {
    if (wasAllGraded || !allItemsGraded(updated)) return
    if (isChapterTest) {
      setReportPromptOpen(true)
    } else if (showReportButton) {
      toast.success(t('test.allGradedReportHint', { completeLabel, reportLabel }))
    }
  }
  const detailKey = coachingKeys.testDetail(learnerId, data.testId)
  const saveSheetGrade = (quizId: string, isCorrect: boolean) => {
    if (sheetBatchStartAllGraded.current == null) {
      sheetBatchStartAllGraded.current = allItemsGraded(qc.getQueryData<LcTestDetailView>(detailKey))
    }
    return sheetGrade.mutateAsync({ quizId, isCorrect, manualGrade: true })
  }
  const handleSheetSaveSettled = () => {
    // 문항별 저장에서는 목록 갱신을 껐으므로 여기서 한 번만(부분 실패·재시도 포함) 트리 상태·평균을 갱신한다.
    qc.invalidateQueries({ queryKey: coachingKeys.tests(learnerId) })
    const wasAllGraded = sheetBatchStartAllGraded.current ?? isAllGraded
    sheetBatchStartAllGraded.current = null
    announceIfNewlyAllGraded(wasAllGraded, qc.getQueryData<LcTestDetailView>(detailKey))
  }
  const requestCoaching = (quizId: string) => {
    setGradingMode('coaching')
    setFocusRequest({ quizId, nonce: Date.now() })
  }

  return (
    <section className="space-y-3" data-testid="test-coaching-detail">
      {showContentManagement ? (
        <ContentManagementPanel
          fields={[
            { label: t('contentManagement.field.testRound'), value: data.roundNo },
            ...(data.kind === 'CHAPTER'
              ? [
                  { label: t('contentManagement.field.chapterNumber'), value: data.chapterOrdinal },
                  { label: t('contentManagement.field.chapterTitle'), value: selection.chapterTitle ?? '-' },
                ]
              : []),
            { label: t('contentManagement.field.questionCount'), value: data.items.length },
          ]}
        />
      ) : null}

      <section className="rounded-lg border border-gray-200 bg-white" data-testid="content-body">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            <span
              data-testid="test-status"
              className={clsx(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                data.status === 'COMPLETED'
                  ? 'bg-lc-mint-100 text-lc-mint-900'
                  : 'bg-amber-50 text-amber-700',
              )}
            >
              {t(`test.statusLabel.${statusKey}`)}
            </span>
            {data.score != null && (
              <span className="text-xs font-medium text-gray-600">{t('test.score', { score: data.score })}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <GradingModeSwitch mode={gradingMode} onChange={setGradingMode} disabled={readOnly} />
            <button
              type="button"
              data-testid="test-print-btn"
              onClick={() =>
                window.open(
                  `/admin/coaching/print/${learnerId}/${data.scopeContentId}/${data.kind === 'CHAPTER_COMPREHENSIVE' ? 'cumulative' : 'chapter'}?testId=${data.testId}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
              className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('test.printQuestions')}
            </button>
            <LmsPrintPreviewPdfButton
              href={`/admin/coaching/print/${learnerId}/${data.scopeContentId}/${data.kind === 'CHAPTER_COMPREHENSIVE' ? 'cumulative' : 'chapter'}?testId=${data.testId}`}
            />
            {showReportButton ? (
              <button
                type="button"
                data-testid="test-report-btn"
                disabled={!canEditReport}
                title={!canEditReport ? t('test.reportDisabledHint') : undefined}
                onClick={openReportEditor}
                className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {reportLabel}
              </button>
            ) : null}
            {data.status === 'COMPLETED' && allowRetake ? (
              <button
                type="button"
                data-testid="test-retake-btn"
                onClick={() => retake.mutate(data.testId, {
                  onSuccess: (result) => setRetakeTestId(result.testId),
                })}
                disabled={retake.isPending}
                className="rounded-md bg-primary-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
              >
                {retake.isPending ? t('test.retaking') : t('test.retake')}
              </button>
            ) : data.status !== 'COMPLETED' ? (
              <button
                type="button"
                data-testid="test-complete-btn"
                onClick={() => { if (!readOnly) complete.mutate(undefined, { onSuccess: onChanged }) }}
                disabled={complete.isPending || !canCompleteTest}
                title={!canCompleteTest ? t('test.completeDisabledHint') : undefined}
                className="rounded-md bg-primary-600 px-3 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {complete.isPending
                  ? t('test.completing')
                  : completeLabel}
              </button>
            ) : null}
          </div>
        </header>
        {retake.error && (
          <p className="px-4 py-2 text-xs text-red-600" role="alert">{t('test.retakeError')}</p>
        )}

        {gradingMode === 'grading' ? (
          <div className="p-4">
            <TestGradingSheet
              test={data}
              canGrade={!readOnly}
              saveGrade={saveSheetGrade}
              onSaveSettled={handleSheetSaveSettled}
              onRequestCoaching={requestCoaching}
            />
          </div>
        ) : (
          <QuestionGradingList
            presentation="single"
            items={data.items.map(toQuestionGradingItem)}
            isSubmitting={grade.isPending}
            canGrade={!readOnly}
            initialSelectedQuizId={focusRequest?.quizId ?? null}
            focusRequest={focusRequest}
            onGrade={({ quizId, answers, isCorrect, manualGrade }) => {
              return grade.mutateAsync({
                quizId,
                answer: JSON.stringify(answers),
                isCorrect,
                manualGrade,
              }).then((updated) => {
                announceIfNewlyAllGraded(isAllGraded, updated)
                return updated
              })
            }}
          />
        )}
      </section>

      <ConfirmDialog
        open={reportPromptOpen}
        title={t('test.reportPrompt.title')}
        message={t('test.reportPrompt.message')}
        confirmLabel={t('test.reportPrompt.confirm')}
        cancelLabel={t('test.reportPrompt.cancel')}
        pending={false}
        tone="primary"
        onCancel={() => setReportPromptOpen(false)}
        onConfirm={() => {
          setReportPromptOpen(false)
          openReportEditor()
        }}
      >
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-900">{t('test.reportPrompt.itemsTitle')}</h3>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-gray-700 marker:text-primary-500">
            <li>{t('test.reportPrompt.reviewLessons')}</li>
            <li>{t('test.reportPrompt.overallComment')}</li>
            <li>{t('test.reportPrompt.growthRecords')}</li>
          </ul>
        </div>
        <p className="mt-4 text-sm leading-6 text-gray-600">{t('test.reportPrompt.footer')}</p>
      </ConfirmDialog>
    </section>
  )
}
